/**
 * Appearance preferences — portal theme, Net Exposure cards, table density.
 * Persisted in localStorage; applied on <html> as data-* attributes.
 * Default: dark portal, white Net Exposure cards (Agents / Lab Backend-UI parity).
 */

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Button, Modal, Segmented } from '@w7/ui'

export type PortalTheme = 'dark' | 'light'
export type NeCardSurface = 'dark' | 'light'
/** Global row density for every `.w7-table` unless a table sets data-density explicitly. */
export type TableDensity = 'compact' | 'default'

export interface ThemePrefs {
  theme: PortalTheme
  neCards: NeCardSurface
  tableDensity: TableDensity
}

const THEME_KEY = 'w7.prefs.theme'
/** v2: default flipped to white; old key ignored so prior implicit dark does not stick. */
const NE_CARDS_KEY = 'w7.prefs.neCards.v2'
const TABLE_DENSITY_KEY = 'w7.prefs.tableDensity.v2'

export const DEFAULT_THEME_PREFS: ThemePrefs = {
  theme: 'dark',
  neCards: 'light',
  tableDensity: 'compact',
}

export function readThemePrefs(): ThemePrefs {
  try {
    const theme = window.localStorage.getItem(THEME_KEY)
    const neCards = window.localStorage.getItem(NE_CARDS_KEY)
    const tableDensity = window.localStorage.getItem(TABLE_DENSITY_KEY)
    return {
      theme: theme === 'light' ? 'light' : 'dark',
      /* unset → white; only explicit 'dark' opts into dark cards */
      neCards: neCards === 'dark' ? 'dark' : 'light',
      tableDensity: tableDensity === 'default' ? 'default' : 'compact',
    }
  } catch {
    return { ...DEFAULT_THEME_PREFS }
  }
}

export function writeThemePrefs(prefs: ThemePrefs) {
  try {
    window.localStorage.setItem(THEME_KEY, prefs.theme)
    window.localStorage.setItem(NE_CARDS_KEY, prefs.neCards)
    window.localStorage.setItem(TABLE_DENSITY_KEY, prefs.tableDensity)
  } catch {
    /* private mode */
  }
}

export function applyPrefsToDocument(prefs: ThemePrefs, opts?: { animate?: boolean }) {
  const root = document.documentElement
  if (opts?.animate) {
    root.classList.add('w7-theme-animating')
    window.setTimeout(() => root.classList.remove('w7-theme-animating'), 320)
  }
  root.setAttribute('data-theme', prefs.theme)
  root.setAttribute('data-ne-cards', prefs.neCards)
  root.setAttribute('data-table-density', prefs.tableDensity)
  root.style.colorScheme = prefs.theme
  const meta = document.querySelector('meta[name="color-scheme"]')
  if (meta) meta.setAttribute('content', prefs.theme)
  const themeColor = document.querySelector('meta[name="theme-color"]')
  if (themeColor) {
    themeColor.setAttribute('content', prefs.theme === 'light' ? '#f4f4f6' : '#0B0B10')
  }
}

interface ThemeContextValue extends ThemePrefs {
  setTheme: (theme: PortalTheme) => void
  setNeCards: (neCards: NeCardSurface) => void
  setTableDensity: (tableDensity: TableDensity) => void
  openPreferences: () => void
  closePreferences: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function useThemePrefs(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useThemePrefs must be inside ThemeProvider')
  return ctx
}

function PreferencesModal({
  open,
  onClose,
  theme,
  neCards,
  tableDensity,
  setTheme,
  setNeCards,
  setTableDensity,
}: {
  open: boolean
  onClose: () => void
  theme: PortalTheme
  neCards: NeCardSurface
  tableDensity: TableDensity
  setTheme: (theme: PortalTheme) => void
  setNeCards: (neCards: NeCardSurface) => void
  setTableDensity: (tableDensity: TableDensity) => void
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Appearance"
      subtitle="Theme, cards, and table density. Saved on this device."
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="w7-col" style={{ gap: 'var(--w7-s5)' }}>
        <div className="w7-col" style={{ gap: 'var(--w7-s3)' }}>
          <div>
            <p className="w7-label" style={{ marginBottom: 4 }}>
              Portal theme
            </p>
            <p className="w7-card-sub">Applies across every page in the console.</p>
          </div>
          <Segmented
            label="Portal theme"
            value={theme}
            onChange={setTheme}
            items={[
              { id: 'dark', label: 'Dark' },
              { id: 'light', label: 'Light' },
            ]}
          />
        </div>

        <div className="w7-col" style={{ gap: 'var(--w7-s3)' }}>
          <div>
            <p className="w7-label" style={{ marginBottom: 4 }}>
              Table density
            </p>
            <p className="w7-card-sub">
              Compact or default rows for every table (reports, Exposure, Member, …).
            </p>
          </div>
          <Segmented
            label="Table density"
            value={tableDensity}
            onChange={setTableDensity}
            items={[
              { id: 'default', label: 'Default' },
              { id: 'compact', label: 'Compact' },
            ]}
          />
        </div>

        <div className="w7-col" style={{ gap: 'var(--w7-s3)' }}>
          <div>
            <p className="w7-label" style={{ marginBottom: 4 }}>
              Net Exposure cards
            </p>
            <p className="w7-card-sub">
              Odds + hierarchy cards on Detail Book, and event tables on Net Exposure.
            </p>
          </div>
          <Segmented
            label="Net Exposure cards"
            value={neCards}
            onChange={setNeCards}
            items={[
              { id: 'dark', label: 'Dark' },
              { id: 'light', label: 'White' },
            ]}
          />
        </div>
      </div>
    </Modal>
  )
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<ThemePrefs>(() =>
    typeof window === 'undefined' ? DEFAULT_THEME_PREFS : readThemePrefs(),
  )
  const [open, setOpen] = useState(false)
  const firstApply = useRef(true)

  useLayoutEffect(() => {
    applyPrefsToDocument(prefs, { animate: !firstApply.current })
    firstApply.current = false
    writeThemePrefs(prefs)
  }, [prefs])

  const setTheme = useCallback((theme: PortalTheme) => {
    setPrefs((prev) => ({ ...prev, theme }))
  }, [])

  const setNeCards = useCallback((neCards: NeCardSurface) => {
    setPrefs((prev) => ({ ...prev, neCards }))
  }, [])

  const setTableDensity = useCallback((tableDensity: TableDensity) => {
    setPrefs((prev) => ({ ...prev, tableDensity }))
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...prefs,
      setTheme,
      setNeCards,
      setTableDensity,
      openPreferences: () => setOpen(true),
      closePreferences: () => setOpen(false),
    }),
    [prefs, setTheme, setNeCards, setTableDensity],
  )

  return (
    <ThemeContext.Provider value={value}>
      {children}
      <PreferencesModal
        open={open}
        onClose={() => setOpen(false)}
        theme={prefs.theme}
        neCards={prefs.neCards}
        tableDensity={prefs.tableDensity}
        setTheme={setTheme}
        setNeCards={setNeCards}
        setTableDensity={setTableDensity}
      />
    </ThemeContext.Provider>
  )
}
